// frontend/src/utils/logger.js
// Safe logging utility that filters sensitive data in production

const isProduction = import.meta.env.PROD || import.meta.env.MODE === 'production';

// Patterns to detect and redact sensitive information
const sensitivePatterns = [
  // Passwords
  /password["\s:=]+([^"'\s,}]+)/gi,
  /pwd["\s:=]+([^"'\s,}]+)/gi,
  
  // Tokens and API keys
  /token["\s:=]+([^"'\s,}]+)/gi,
  /api[_-]?key["\s:=]+([^"'\s,}]+)/gi,
  /authorization["\s:=]+([^"'\s,}]+)/gi,
  /bearer\s+([^\s"']+)/gi,
  /jwt["\s:=]+([^"'\s,}]+)/gi,
  
  // Secrets
  /secret["\s:=]+([^"'\s,}]+)/gi,
  /secret[_-]?key["\s:=]+([^"'\s,}]+)/gi,
  
  // Credit card numbers
  /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
  
  // SSN
  /\b\d{3}-\d{2}-\d{4}\b/g,
];

// Redact sensitive data from strings
function redactSensitiveData(data) {
  if (typeof data === 'string') {
    let redacted = data;
    sensitivePatterns.forEach(pattern => {
      redacted = redacted.replace(pattern, (match, value) => {
        if (value && value.length > 0) {
          // Replace with redacted version
          const redactedValue = value.length > 8 
            ? `${value.substring(0, 2)}***${value.substring(value.length - 2)}`
            : '***';
          return match.replace(value, redactedValue);
        }
        return match;
      });
    });
    return redacted;
  }
  
  if (typeof data === 'object' && data !== null) {
    if (Array.isArray(data)) {
      return data.map(item => redactSensitiveData(item));
    }
    
    // Deep clone and redact object properties
    const redacted = {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      
      // Skip sensitive keys entirely in production
      if (isProduction && (
        lowerKey.includes('password') ||
        lowerKey.includes('token') ||
        lowerKey.includes('secret') ||
        (lowerKey.includes('api') && lowerKey.includes('key')) ||
        lowerKey.includes('authorization')
      )) {
        redacted[key] = '[REDACTED]';
        continue;
      }
      
      redacted[key] = redactSensitiveData(value);
    }
    return redacted;
  }
  
  return data;
}

// Safe logger that filters sensitive data in production
const logger = {
  log: (...args) => {
    if (!isProduction) {
      console.log(...args);
    } else {
      const redacted = args.map(arg => redactSensitiveData(arg));
      console.log(...redacted);
    }
  },
  
  error: (...args) => {
    // Always log errors, but redact sensitive data
    const redacted = args.map(arg => redactSensitiveData(arg));
    console.error(...redacted);
  },
  
  warn: (...args) => {
    if (!isProduction) {
      console.warn(...args);
    } else {
      const redacted = args.map(arg => redactSensitiveData(arg));
      console.warn(...redacted);
    }
  },
  
  info: (...args) => {
    if (!isProduction) {
      console.info(...args);
    } else {
      const redacted = args.map(arg => redactSensitiveData(arg));
      console.info(...redacted);
    }
  },
  
  debug: (...args) => {
    // Only log debug in development
    if (!isProduction) {
      console.debug(...args);
    }
  }
};

export default logger;
